import React, { useEffect, useState, useCallback } from "react";
import { Sparkles, Coins, Clock, Check, X as XIcon, Trash2, Eye, EyeOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import api from "../../lib/apiClient";

// OffersTab — buyer (sent) + seller (received) views of video offers.
// Strictly hides offerer identity in the Received list (server-enforced
// too — this is defense in depth).

const fmtCountdown = (expiresISO) => {
  if (!expiresISO) return "";
  const ms = new Date(expiresISO).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const hrs = Math.floor(ms / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return hrs > 0 ? `${hrs}h ${mins}m left` : `${mins}m left`;
};

const ReceivedOfferCard = ({ offer, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const v = offer.video || {};

  const act = async (action) => {
    setBusy(action);
    try {
      const token = localStorage.getItem("globaleyes_token");
      await api.post(`/marketplace/offers/${offer.id}/${action}`,
        {},
      );
      toast.success(action === "accept" ? "Offer accepted — credits sent your way." : "Offer rejected.");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update offer");
    }
    setBusy(null);
  };

  return (
    <Card className="bg-gray-900/60 border-amber-500/30" data-testid={`received-offer-${offer.id}`}>
      <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        {v.thumbnail_url ? (
          <img src={v.thumbnail_url} alt="" className="w-full sm:w-28 aspect-video object-cover rounded shrink-0" />
        ) : (
          <video src={v.video_url} className="w-full sm:w-28 aspect-video object-cover rounded shrink-0 bg-black" muted />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-0.5 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Anonymous offer
          </div>
          <h4 className="text-white font-semibold text-sm truncate">{v.title || "(deleted)"}</h4>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
            <span className="flex items-center gap-1 text-amber-300 font-bold">
              <Coins className="h-3.5 w-3.5" />
              {offer.amount_credits.toLocaleString()} cr
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {fmtCountdown(offer.expires_at)}
            </span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            size="sm"
            onClick={() => act("accept")}
            disabled={!!busy}
            className="bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold"
            data-testid={`accept-offer-${offer.id}`}
          >
            {busy === "accept" ? "…" : <><Check className="h-3.5 w-3.5 mr-1" /> Accept</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => act("reject")}
            disabled={!!busy}
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
            data-testid={`reject-offer-${offer.id}`}
          >
            {busy === "reject" ? "…" : <><XIcon className="h-3.5 w-3.5 mr-1" /> Reject</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SentOfferCard = ({ offer, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const v = offer.video || {};
  const refundable = new Date(offer.refundable_until).getTime() > Date.now();
  const expired = offer.status === "expired" || new Date(offer.expires_at).getTime() < Date.now();

  const withdraw = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("globaleyes_token");
      const r = await api.delete(`/marketplace/offers/${offer.id}`);
      toast.success(r.data.fee_refunded ? "Offer withdrawn — full refund." : "Offer withdrawn (fee retained).");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't withdraw");
    }
    setBusy(false);
  };

  const badge = {
    pending:  ["bg-amber-500/15 text-amber-300 border-amber-500/40",  "Pending"],
    accepted: ["bg-emerald-500/15 text-emerald-300 border-emerald-500/40", "Accepted ✓"],
    rejected: ["bg-gray-700/40 text-gray-400 border-gray-700",  "Rejected"],
    expired:  ["bg-gray-700/40 text-gray-500 border-gray-700",  "Expired"],
    withdrawn:["bg-gray-700/40 text-gray-500 border-gray-700",  "Withdrawn"],
  }[offer.status] || ["bg-gray-700 text-gray-300", offer.status];

  return (
    <Card className="bg-gray-900/60 border-gray-800" data-testid={`sent-offer-${offer.id}`}>
      <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        {v.thumbnail_url ? (
          <img src={v.thumbnail_url} alt="" className="w-full sm:w-28 aspect-video object-cover rounded shrink-0" />
        ) : (
          <video src={v.video_url} className="w-full sm:w-28 aspect-video object-cover rounded shrink-0 bg-black" muted />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-semibold text-sm truncate">{v.title || "(deleted)"}</h4>
          <p className="text-gray-500 text-xs">on @{v.owner_username || "unknown"}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
            <span className="flex items-center gap-1 text-amber-300 font-bold">
              <Coins className="h-3.5 w-3.5" />
              {offer.amount_credits.toLocaleString()} cr
            </span>
            {!expired && offer.status === "pending" && (
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {fmtCountdown(offer.expires_at)}
              </span>
            )}
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${badge[0]}`}>
              {badge[1]}
            </span>
          </div>
        </div>
        {offer.status === "pending" && (
          <Button
            size="sm"
            variant="outline"
            onClick={withdraw}
            disabled={busy}
            className="border-gray-700 text-gray-300 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/40"
            data-testid={`withdraw-offer-${offer.id}`}
            title={refundable ? "Full refund within 1h" : "1cr fee retained"}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            {busy ? "…" : refundable ? "Withdraw (full refund)" : "Withdraw"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const OffersTab = ({ token, refreshUser }) => {
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptOffers, setAcceptOffers] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s, me] = await Promise.all([
        api.get(`/marketplace/offers/received`),
        api.get(`/marketplace/offers/sent`),
        api.get(`/auth/me`),
      ]);
      setReceived(r.data || []);
      setSent(s.data || []);
      setAcceptOffers(me.data.accept_offers !== false);
    } catch {
      toast.error("Couldn't load offers");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggleAcceptOffers = async () => {
    try {
      const r = await api.patch(`/marketplace/accept-offers`,
        { accept_offers: !acceptOffers },
      );
      setAcceptOffers(r.data.accept_offers);
      toast.success(r.data.accept_offers ? "Now accepting offers." : "Offers paused.");
    } catch {
      toast.error("Couldn't toggle");
    }
  };

  const onChanged = async () => {
    await load();
    if (refreshUser) await refreshUser();
  };

  if (loading) return <p className="text-gray-500 text-sm" data-testid="offers-loading">Loading offers…</p>;

  return (
    <div className="space-y-5" data-testid="offers-tab">
      <Card className="bg-gray-900/60 border-gray-800">
        <CardContent className="p-3 sm:p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-white text-sm font-medium">Accept anonymous offers</p>
            <p className="text-gray-500 text-xs">Turn this off to stop receiving offers on any video you own.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAcceptOffers}
            className={`shrink-0 ${acceptOffers ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "border-gray-700 text-gray-400"}`}
            data-testid="accept-offers-toggle"
          >
            {acceptOffers ? <><Eye className="h-3.5 w-3.5 mr-1" /> Receiving</> : <><EyeOff className="h-3.5 w-3.5 mr-1" /> Paused</>}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-gray-900/60 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-300" /> Received offers
            <span className="text-gray-500 text-xs font-normal ml-1">({received.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {received.length === 0 ? (
            <p className="text-gray-500 text-sm italic">
              No offers right now. Anyone can make an anonymous offer on your videos — you'll see them here.
            </p>
          ) : (
            received.map((o) => <ReceivedOfferCard key={o.id} offer={o} onChanged={onChanged} />)
          )}
        </CardContent>
      </Card>

      <Card className="bg-gray-900/60 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Coins className="h-4 w-4 text-emerald-400" /> Offers you've made
            <span className="text-gray-500 text-xs font-normal ml-1">({sent.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {sent.length === 0 ? (
            <div className="bg-gray-800/40 border border-gray-700 rounded p-3 flex items-start gap-2.5 text-xs text-gray-400">
              <AlertCircle className="h-4 w-4 mt-0.5 text-gray-500 shrink-0" />
              <p>
                Want a video? Open it from /map or any feed, then tap{" "}
                <span className="text-amber-300 font-medium">Make an anonymous offer</span>.
              </p>
            </div>
          ) : (
            sent.map((o) => <SentOfferCard key={o.id} offer={o} onChanged={onChanged} />)
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OffersTab;
